/**
 * Create User Component
 * Form for creating new users (admin only)
 */

import React, { useState } from 'react';
import { adminAPI } from '../../services/api';

interface CreateUserProps {
  onSuccess?: (user: any, tempPassword: string) => void;
  onCancel?: () => void;
}

export const CreateUser: React.FC<CreateUserProps> = ({ onSuccess, onCancel }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'USER'>('USER');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ user: any; tempPassword: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setCreating(true);
      setError(null);

      const response = await adminAPI.createUser(email, role);

      setSuccess({
        user: response.user,
        tempPassword: response.tempPassword
      });

      if (onSuccess) {
        onSuccess(response.user, response.tempPassword);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleReset = () => {
    setEmail('');
    setRole('USER');
    setError(null);
    setSuccess(null);
  };

  if (success) {
    return (
      <div className="bg-white rounded-lg p-6 space-y-6">
        {/* Success Message */}
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">
            User Created Successfully!
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            A welcome email has been sent to the user with their temporary password.
          </p>
        </div>

        {/* User Details */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-500">Email</label>
            <p className="text-sm font-medium text-gray-900">{success.user.email}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Role</label>
            <p className="text-sm font-medium text-gray-900">{success.user.role}</p>
          </div>
        </div>

        {/* Temporary Password */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex gap-2">
            <svg
              className="h-5 w-5 text-yellow-600 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4 className="font-medium text-yellow-900 mb-1">
                Temporary Password
              </h4>
              <p className="text-sm text-yellow-800 mb-2">
                Save this password now. It won't be shown again.
              </p>
              <div className="bg-white border border-yellow-300 rounded px-3 py-2 font-mono text-sm">
                {success.tempPassword}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(success.tempPassword);
                  alert('Password copied to clipboard');
                }}
                className="mt-2 text-yellow-700 hover:text-yellow-800 underline text-sm"
              >
                Copy to clipboard
              </button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Create Another User
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6 space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Create New User
        </h3>
        <p className="text-sm text-gray-600">
          Create a new user account. A temporary password will be generated and sent via email.
        </p>
      </div>

      {/* Email Input */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address *
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="user@example.com"
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Role Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Role *
        </label>
        <div className="grid grid-cols-2 gap-4">
          <label
            className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
              role === 'USER'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="role"
              value="USER"
              checked={role === 'USER'}
              onChange={(e) => setRole(e.target.value as 'USER')}
              className="mr-3"
            />
            <div>
              <div className="font-medium text-gray-900">User</div>
              <div className="text-xs text-gray-600">
                Can generate statements and manage templates
              </div>
            </div>
          </label>

          <label
            className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
              role === 'ADMIN'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="role"
              value="ADMIN"
              checked={role === 'ADMIN'}
              onChange={(e) => setRole(e.target.value as 'ADMIN')}
              className="mr-3"
            />
            <div>
              <div className="font-medium text-gray-900">Admin</div>
              <div className="text-xs text-gray-600">
                Full access including user management
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-2">
          <svg
            className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-xs text-blue-800">
            A secure temporary password will be automatically generated.
            The user will receive a welcome email with instructions to log in.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={creating}
          className={`flex-1 py-2 px-4 rounded-lg font-medium text-white transition-colors ${
            creating
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {creating ? 'Creating...' : 'Create User'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={creating}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};
